import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { RailItem } from "@/hooks/use-onboarding-step";
import type { OnboardingStepKey } from "@/types/onboarding";
import { focusRing } from "./ui/buttons";

interface OnboardingRailProps {
  items: RailItem[];
  onClick: (key: OnboardingStepKey) => void;
}

/** The six steps down the left: a numbered circle, label and subline, joined by a line. */
export function OnboardingRail({ items, onClick }: OnboardingRailProps) {
  const { t } = useTranslation("onboarding");
  return (
    <ol className="flex flex-col" aria-label={t("meta.kicker")}>
      {items.map((item, index) => {
        const last = index === items.length - 1;
        const label = t(`rail.${item.key}.label`);
        const sub = t(`rail.${item.key}.sub`);
        const content = (
          <>
            <span
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-g-pill border text-[12px] font-bold tabular-nums transition-colors",
                item.state === "done" && "border-g-primary bg-g-primary text-g-on-primary",
                item.state === "current" && "border-g-primary bg-g-bg-card text-g-primary",
                item.state === "upcoming" && "border-g-line-strong bg-g-bg-card text-g-ink-400",
              )}
              aria-hidden="true"
            >
              {item.state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index + 1}
            </span>
            <span className="flex min-w-0 flex-col">
              <span
                className={cn(
                  "text-[14px] font-semibold",
                  item.state === "upcoming" ? "text-g-ink-500" : "text-g-ink-900",
                )}
              >
                {label}
              </span>
              <span className="text-[12.5px] text-g-ink-500">{sub}</span>
            </span>
          </>
        );
        return (
          <li key={item.key} className="relative flex gap-3 pb-6 last:pb-0">
            {!last ? (
              <span
                className={cn(
                  "absolute left-[13px] top-8 h-[calc(100%-1.25rem)] w-px",
                  item.state === "done" ? "bg-g-primary-muted" : "bg-g-line-strong",
                )}
                aria-hidden="true"
              />
            ) : null}
            {item.clickable ? (
              <button
                type="button"
                onClick={() => onClick(item.key)}
                aria-current={item.state === "current" ? "step" : undefined}
                className={cn(
                  "-m-1.5 flex flex-1 items-start gap-3 rounded-g-sm p-1.5 text-left transition-colors hover:bg-g-primary-subtle",
                  focusRing,
                )}
              >
                {content}
              </button>
            ) : (
              <span
                className="flex flex-1 items-start gap-3"
                aria-current={item.state === "current" ? "step" : undefined}
              >
                {content}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Compact variant for narrow screens: six dots and nothing else. */
export function OnboardingDots({ items }: { items: RailItem[] }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {items.map((item) => (
        <span
          key={item.key}
          className={cn(
            "h-1.5 rounded-g-pill transition-all duration-200",
            item.state === "current" ? "w-5 bg-g-primary" : "w-1.5",
            item.state === "done" && "bg-g-primary-muted",
            item.state === "upcoming" && "bg-g-line-strong",
          )}
        />
      ))}
    </div>
  );
}
